import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Loader2, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FACTOR_LABELS = {
  reliability: 'Reliability',
  skill_quality: 'Skill Quality',
  professionalism: 'Professionalism',
  collaboration: 'Collaboration',
};

function InsightsDialog({ open, onOpenChange, insights, loading }) {
  if (!insights && !loading) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="insights-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Shield className="w-4 h-4 text-[hsl(36,80%,52%)]" />
            Trust Profile
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : insights ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{insights.entity_name}</span>
              <Badge className="bg-[hsl(36,80%,52%)] text-white border-0 text-xs gap-1">
                <Shield className="w-3 h-3" /> {insights.trust_score}
              </Badge>
            </div>
            <div className="space-y-2.5">
              {Object.entries(insights.dimensions || {}).slice(0, 4).map(([key, score]) => (
                <div key={key} data-testid={`insight-${key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">{FACTOR_LABELS[key] || key}</span>
                    <span className="text-xs font-medium">{score}</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-secondary">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${score}%`,
                        backgroundColor: score >= 85 ? 'hsl(36,80%,52%)' : score >= 70 ? 'hsl(36,60%,60%)' : 'hsl(0,0%,70%)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {insights.insight && (
              <p className="text-xs text-muted-foreground leading-relaxed pt-3 border-t border-border/50" data-testid="trust-text-insight">
                {insights.insight}
              </p>
            )}
            <p className="text-[9px] text-muted-foreground/60 text-right">Curated by {insights.generated_by}</p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function AinaaBadge({ ainaaId, trustScore, className = '' }) {
  const [open, setOpen] = useState(false);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const hasTrust = typeof trustScore === 'number' && trustScore > 0;

  const fetchInsights = useCallback(async () => {
    if (!ainaaId || !hasTrust) return;
    setOpen(true);
    setLoading(true);
    try {
      const res = await fetch(`${API}/spectre/insights/${ainaaId}`);
      if (res.ok) setInsights(await res.json());
    } catch { /* handled */ }
    setLoading(false);
  }, [ainaaId, hasTrust]);

  return (
    <>
      <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid={`ainaa-badge-${ainaaId}`}>
        <span className="text-[10px] font-mono text-muted-foreground tracking-wide">{ainaaId}</span>
        {hasTrust && (
          <button
            onClick={(e) => { e.stopPropagation(); fetchInsights(); }}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[hsl(36,80%,52%)]/15 text-[hsl(36,80%,42%)] text-[10px] font-medium hover:bg-[hsl(36,80%,52%)]/25 transition-colors cursor-pointer"
            data-testid={`trust-pill-${ainaaId}`}
          >
            <Shield className="w-2.5 h-2.5" strokeWidth={2.5} />
            {trustScore}
          </button>
        )}
      </span>
      <InsightsDialog open={open} onOpenChange={setOpen} insights={insights} loading={loading} />
    </>
  );
}

export default AinaaBadge;
